import { useEffect, useState } from "react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Flame, Trophy, Zap, Sparkles, Loader2, Plus, RefreshCw } from "lucide-react";
import AvailabilityGrid from "@/components/AvailabilityGrid";

const BADGE_STYLE = {
  "First Win": { bg: "#BED445", icon: Trophy },
  "10 Matches": { bg: "#4A90E2", icon: Sparkles },
  "Hot Streak": { bg: "#F5B700", icon: Flame },
  "Court Killer": { bg: "#0E395F", icon: Zap },
  "Ladder Champ": { bg: "#005F19", icon: Trophy },
};

export default function Passport() {
  const { user, refresh } = useAuth();
  const [matches, setMatches] = useState([]);
  const [players, setPlayers] = useState([]);
  const [logOpen, setLogOpen] = useState(false);
  const [form, setForm] = useState({ opponent_id: "", my_score: 11, opponent_score: 7, match_type: "singles" });
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    try {
      const [m, p] = await Promise.all([http.get("/matches"), http.get("/users")]);
      setMatches(m.data);
      setPlayers(p.data.filter(u => u.user_id !== user?.user_id));
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { if (user) load(); }, [user]);

  if (!user) return <Loader2 className="animate-spin"/>;

  const chartData = (user.rating_history || []).map(r => ({
    date: new Date(r.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    rating: r.rating,
  }));

  const total = (user.wins || 0) + (user.losses || 0);
  const winPct = total ? Math.round((user.wins / total) * 100) : 0;

  const logMatch = async () => {
    if (!form.opponent_id) { toast.error("Pick an opponent"); return; }
    try {
      await http.post("/matches/log", form);
      toast.success("Match logged. Awaiting opponent confirmation.");
      setLogOpen(false);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const confirmMatch = async (m) => {
    try {
      await http.post(`/matches/${m.match_id}/confirm`);
      toast.success("Confirmed. Ratings updated.");
      load(); refresh();
    } catch (e) { toast.error(formatError(e)); }
  };

  const duprSync = async () => {
    setSyncing(true);
    try {
      const { data } = await http.post("/dupr/sync");
      toast.success(data.message);
    } catch (e) { toast.error(formatError(e)); }
    setSyncing(false);
  };

  const badges = user.badges || [];

  return (
    <div className="space-y-6" data-testid="passport-page">
      <section className="card p-6 rise-in">
        <div className="flex flex-col md:flex-row md:items-center gap-5">
          <Avatar className="h-24 w-24 border-4 pv-border-primary">
            <AvatarImage src={user.avatar}/><AvatarFallback>{user.name?.[0]}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl sm:text-3xl font-display font-extrabold pv-text-dark">{user.name}</h1>
              <span className="pill pv-bg-primary text-black">DUPR {user.dupr_rating}</span>
              {user.streak >= 3 && <span className="streak-badge text-xs"><Flame size={12}/> {user.streak} win streak</span>}
            </div>
            <div className="text-sm text-gray-600 mt-1">{user.bio || "Just here to dink."}  · {user.location} · {user.play_style}</div>
            <div className="mt-3 grid grid-cols-3 gap-3 max-w-md">
              <Stat label="Wins" value={user.wins || 0}/>
              <Stat label="Losses" value={user.losses || 0}/>
              <Stat label="Win %" value={`${winPct}%`}/>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <button data-testid="log-match-btn" onClick={()=>setLogOpen(true)} className="btn-primary flex items-center justify-center gap-1"><Plus size={16}/> Log match</button>
            <button data-testid="dupr-sync-btn" onClick={duprSync} disabled={syncing} className="btn-support flex items-center justify-center gap-2">
              {syncing ? <Loader2 size={16} className="animate-spin"/> : <RefreshCw size={16}/>} Sync to DUPR
            </button>
          </div>
        </div>
      </section>

      <section className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 card p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-bold text-lg pv-text-dark">Rating trajectory</h2>
            <span className="text-xs text-gray-500">Last 12 checkpoints</span>
          </div>
          <div style={{width:"100%", height: 240}}>
            <ResponsiveContainer>
              <LineChart data={chartData}>
                <CartesianGrid stroke="#eee" strokeDasharray="3 3"/>
                <XAxis dataKey="date" fontSize={11} stroke="#666"/>
                <YAxis domain={["auto","auto"]} fontSize={11} stroke="#666"/>
                <Tooltip contentStyle={{borderRadius:12}}/>
                <Line type="monotone" dataKey="rating" stroke="#4A90E2" strokeWidth={3} dot={{ r: 5, fill: "#BED445", stroke: "#0E395F", strokeWidth: 2 }} activeDot={{ r: 7 }}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card p-5" data-testid="badges-section">
          <h2 className="font-display font-bold text-lg pv-text-dark mb-3">Badges</h2>
          {badges.length === 0 ? <div className="text-sm text-gray-500">Win a match to unlock your first badge.</div> : (
            <div className="grid grid-cols-2 gap-3">
              {badges.map((b) => {
                const cfg = BADGE_STYLE[b] || { bg: "#4A90E2", icon: Sparkles };
                const Icon = cfg.icon;
                return (
                  <div key={b} className="rounded-xl p-3 text-white flex flex-col items-center gap-1 text-center" style={{background: cfg.bg, color: b==="Hot Streak"||b==="First Win"?"#000":"#fff"}} data-testid={`badge-${b.replace(/ /g,'-')}`}>
                    <Icon size={22}/>
                    <div className="text-[11px] font-extrabold uppercase tracking-wider">{b}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <AvailabilityGrid />

      <section className="card p-5">
        <h2 className="font-display font-bold text-lg pv-text-dark mb-3">Match log</h2>
        {matches.length === 0 ? <div className="text-sm text-gray-500">No matches yet. Go log one.</div> : (
          <div className="divide-y">
            {matches.slice(0, 15).map((m) => {
              const opp = m.player_a_id === user.user_id ? m.player_b : m.player_a;
              const myScore = m.player_a_id === user.user_id ? m.score_a : m.score_b;
              const oppScore = m.player_a_id === user.user_id ? m.score_b : m.score_a;
              const won = m.winner_id === user.user_id;
              const canConfirm = !m.confirmed && m.logged_by !== user.user_id;
              return (
                <div key={m.match_id} className="py-3 flex items-center gap-3" data-testid={`match-row-${m.match_id}`}>
                  <Avatar className="h-9 w-9"><AvatarImage src={opp?.avatar}/><AvatarFallback>{opp?.name?.[0]}</AvatarFallback></Avatar>
                  <div className="flex-1">
                    <div className="text-sm font-bold pv-text-dark">vs {opp?.name}</div>
                    <div className="text-xs text-gray-500 capitalize">{m.match_type} · {new Date(m.played_at).toLocaleDateString()}</div>
                  </div>
                  <div className={`text-sm font-extrabold ${won?"pv-text-deep-green":"text-red-500"}`}>{myScore}-{oppScore} {won?"W":"L"}</div>
                  {m.confirmed ? <span className="pill pv-bg-primary text-black">Final</span>
                    : canConfirm ? <button data-testid={`confirm-${m.match_id}`} onClick={()=>confirmMatch(m)} className="btn-challenge text-xs py-1">Confirm</button>
                    : <span className="pill pv-bg-highlight text-black">Pending</span>}
                </div>
              );
            })}
          </div>
        )}
      </section>

      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent data-testid="log-match-dialog">
          <DialogHeader><DialogTitle>Log a match</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Opponent</Label>
              <Select value={form.opponent_id} onValueChange={(v)=>setForm(f=>({...f,opponent_id:v}))}>
                <SelectTrigger data-testid="log-opponent-select"><SelectValue placeholder="Pick opponent"/></SelectTrigger>
                <SelectContent className="max-h-72">{players.map(p => <SelectItem key={p.user_id} value={p.user_id}>{p.name} · DUPR {p.dupr_rating}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Match type</Label>
              <Select value={form.match_type} onValueChange={(v)=>setForm(f=>({...f,match_type:v}))}>
                <SelectTrigger data-testid="log-type-select"><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem value="singles">Singles</SelectItem><SelectItem value="doubles">Doubles</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Your score</Label><Input data-testid="log-my-score" type="number" value={form.my_score} onChange={(e)=>setForm(f=>({...f,my_score:+e.target.value}))}/></div>
              <div><Label>Their score</Label><Input data-testid="log-opp-score" type="number" value={form.opponent_score} onChange={(e)=>setForm(f=>({...f,opponent_score:+e.target.value}))}/></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setLogOpen(false)}>Cancel</Button>
            <button data-testid="submit-log-btn" onClick={logMatch} className="btn-primary">Log match</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl bg-[#F8F6F3] p-3 text-center border">
      <div className="text-xl font-display font-extrabold pv-text-dark">{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">{label}</div>
    </div>
  );
}
